import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import { PipelineData } from "../types";
import { InfoTooltip } from "./InfoTooltip";

interface PipelineChartProps {
  data: PipelineData;
}

export const PipelineChart: React.FC<PipelineChartProps> = ({ data }) => {
  const theme = useTheme();
  const outcomes = data.completed + data.failed || 1;
  const completedPct = (data.completed / outcomes) * 100;
  const failedPct = (data.failed / outcomes) * 100;

  const segments = [
    { label: "Created", value: data.created, color: theme.palette.info.main, description: "Total report generation jobs initiated by users." },
    { label: "Completed", value: data.completed, color: theme.palette.success.main, description: "Jobs that successfully produced a report artifact." },
    { label: "Failed", value: data.failed, color: theme.palette.error.main, description: "Jobs that failed during generation (AI, template, or infrastructure errors)." },
    { label: "Retried", value: data.retried, color: theme.palette.warning.main, description: "Jobs that were manually retried by users after an initial failure." },
  ];

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Report Pipeline
            </Typography>
            <InfoTooltip title="Overview of the report generation pipeline from job creation to completion. The donut shows the success rate (completed / (completed + failed))." />
          </Box>
        }
        subheader="Job creation to completion"
      />
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {/* Donut */}
          <Box
            sx={{
              width: 140,
              height: 140,
              borderRadius: "50%",
              background: `
                radial-gradient(circle at center, ${theme.palette.background.paper} 52%, transparent 53%),
                conic-gradient(
                  ${theme.palette.success.main} 0deg ${completedPct * 3.6}deg,
                  ${theme.palette.error.main} ${completedPct * 3.6}deg ${(completedPct + failedPct) * 3.6}deg,
                  ${alpha(theme.palette.grey[400], 0.2)} ${(completedPct + failedPct) * 3.6}deg 360deg
                )
              `,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 0 0 6px ${alpha(theme.palette.success.main, 0.08)}`,
              transition: "all 0.3s ease",
            }}
          >
            <Box
              sx={{
                width: 82,
                height: 82,
                borderRadius: "50%",
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h5" fontWeight={700} color="success.main">
                {data.created > 0 ? Math.round(completedPct) : 0}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Success
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Legend */}
        <Stack spacing={1}>
          {segments.map((s) => (
            <Box
              key={s.label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 0.75,
                borderRadius: 1.5,
                transition: "background-color 0.15s",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: s.color,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                <Typography variant="body2">
                  {s.label}
                </Typography>
                <InfoTooltip title={s.description} />
              </Box>
              <Typography
                variant="body2"
                sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
              >
                {s.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
